import ListHeader from '../../../components/ListHeader';
import TopData from './TopData';
import { useEffect, useMemo, useState } from 'react';
import ChartDirection from './ChartDirection';
import { useTranslation } from 'react-i18next';
import ChartJsBarChart from '../../../components/charts/ChartJsBarChart';
import { useQuery } from '@tanstack/react-query';
import { generalGet } from '../../../API/api';
import UsersOrdersTableContainer from './UsersOrdersTableContainer';
import { IDashboardUserOrder } from '../interfaces';
import ExportExcel from '../../../components/buttons/ExportExcel/ExportExcel';
import TableSkeleton from '../../../components/loaders/TableSkeleton';

interface SalesUsersDataProps {
  salesUsersData: any;
  selectedDuration: string;
}

const SalesUsersData = ({ salesUsersData, selectedDuration }: SalesUsersDataProps) => {
  const { t, i18n } = useTranslation();

  const salesUsers = useMemo(() => {
    const list = salesUsersData?.data?.data as Array<{ sales_id: number; sales_user_name: string; sales_count: number }> | undefined;
    if (!Array.isArray(list)) return [] as Array<{ sales_id: number; sales_user_name: string; sales_count: number }>;
    return list;
  }, [salesUsersData]);

  // Selected user and their orders data
  const [selectedSalesUser, setSelectedSalesUser] = useState<{ id?: number; title?: string }>();

  useEffect(() => {
    if (!selectedSalesUser?.id && salesUsers.length > 0) {
      const top = [...salesUsers].sort((a, b) => Number(b.sales_count) - Number(a.sales_count))[0];
      if (top) setSelectedSalesUser({ id: top.sales_id, title: top.sales_user_name });
    }
  }, [salesUsers, selectedSalesUser?.id]);

  const { data: salesUserOrdersData, isLoading: selectedSalesUserDataLoading } = useQuery({
    queryKey: ['sales-user-orders', i18n.language, selectedSalesUser?.id],
    queryFn: () => generalGet(`/admin/dashboard/sales-user-orders?sales_user_id=${selectedSalesUser?.id}`),
    refetchOnWindowFocus: false,
    enabled: !!selectedSalesUser?.id
  });

  const ordersRows = useMemo(() => {
    const rows = salesUserOrdersData?.data?.data?.data as Array<IDashboardUserOrder> | undefined;
    return Array.isArray(rows) ? rows : [];
  }, [salesUserOrdersData]);

  const tableHeaders = useMemo(() => {
    return [
      { label: '#', customClass: 'actions_col' },
      { label: t('date') },
      { label: t('model') },
      { label: t('colors') },
      { label: t('showroom') },
      { label: t('city') },
      { label: t('paid_amount') },
      { label: t('total') }
    ];
  }, [t]);

  const topData = useMemo(() => {
    if (!salesUsers || salesUsers.length === 0) return [];

    return [...salesUsers]
      .sort((a, b) => Number(b.sales_count) - Number(a.sales_count))
      .slice(0, 10)
      .map((item) => ({
        title: item.sales_user_name,
        number: Number(item.sales_count || 0).toLocaleString('en-US'),
        id: item.sales_id
      }));
  }, [salesUsers]);

  const usersCountData = useMemo(() => {
    if (!salesUsers || salesUsers.length === 0) return [] as Array<{ x: string; fullName: string; value: number }>;

    return salesUsers.map((item) => ({
      x: item.sales_user_name,
      fullName: item.sales_user_name,
      value: Number(item.sales_count || 0)
    }));
  }, [salesUsers]);

  const headerTitle = 'Sales Users';

  return (
    <>
      <ListHeader customClass="row noMargin">
        <h2>{headerTitle}</h2>
      </ListHeader>
      <div className={`dashboard-data no-border`}></div>
      <div className="dashboard-data">
        <div className="top-dashboard-data user-orders">
          <TopData setSelectedProvider={setSelectedSalesUser} data={topData} header={'Top Sales Users'} selectedProvider={selectedSalesUser} />
        </div>
        <div className="charts">
          <ChartDirection>
            <ChartJsBarChart
              data={usersCountData}
              title={'Sales Users Count'}
              dataKey="value"
              fill="#000"
              stroke="#000"
              tooltipLabel={'Count'}
              customTooltipFormatter={(value: any, name: string, props: any) => [`${Number(props.payload?.value || 0).toLocaleString('en-US')}`, t('dashboardCharts.sales_user')]}
              customTickFormatter={(value: any) => `${Number(value).toLocaleString('en-US')}`}
            />
          </ChartDirection>
        </div>
      </div>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h4>{t("user_orders")}</h4>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <ExportExcel
              endpoint="admin/dashboard/sales-user-orders/export"
              fileName="sales-user-orders"
              filters={{
                from_date: selectedDuration?.split('&')?.[0]?.split('=')?.[1],
                to_date: selectedDuration?.split('&')?.[1]?.split('=')?.[1],
                sales_user_id: selectedSalesUser?.id,
                export_type: 'pdf'
              }}
            >
              {t('export_pdf')}
            </ExportExcel>
            <ExportExcel
              endpoint="admin/dashboard/sales-user-orders/export"
              fileName="sales-user-orders"
              filters={{
                from_date: selectedDuration?.split('&')?.[0]?.split('=')?.[1],
                to_date: selectedDuration?.split('&')?.[1]?.split('=')?.[1],
                sales_user_id: selectedSalesUser?.id,
                export_type: 'excel'
              }}
            />
          </div>
        </div>
        {selectedSalesUserDataLoading ? <TableSkeleton withoutHeader withoutButton columns={tableHeaders.length} /> : <UsersOrdersTableContainer tableHeaders={tableHeaders} data={ordersRows} noDataMessage={t('no_data_found')} lessColumns />}
      </div>
    </>
  );
};

export default SalesUsersData;
